import { useState, useEffect, useMemo } from 'react'
import { Card, StatCard, Badge, Loading, Table, Select, Input } from '../components/common'
import { dashboardService } from '../services/dashboardService'
import { ArrowUpDown, Search, List, Grid3X3 } from 'lucide-react'

function WarehousePage() {
  const [stats, setStats] = useState(null)
  const [entries, setEntries] = useState([])
  const [racks, setRacks] = useState([])
  const [byJudul, setByJudul] = useState([])
  const [loading, setLoading] = useState(true)
  const [sortBy, setSortBy] = useState('entry_date_desc') // default sort by newest
  const [filterRack, setFilterRack] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [viewMode, setViewMode] = useState('entries') // 'entries' or 'summary'

  useEffect(() => {
    fetchWarehouseData()
  }, [])

  const fetchWarehouseData = async () => {
    try {
      setLoading(true)
      const response = await dashboardService.getWarehouseStats()
      if (response.success) {
        setStats(response.data.stats)
        setRacks(response.data.byRack || [])
        setEntries(response.data.recentEntries || [])
        setByJudul(response.data.byJudul || [])
      }
    } catch (error) {
      console.error('Error:', error)
    } finally {
      setLoading(false)
    }
  }

  // Get unique rack codes for filter
  const uniqueRacks = useMemo(() => {
    const rackSet = new Set(entries.map(e => e.rack_code).filter(Boolean))
    return Array.from(rackSet).sort()
  }, [entries])

  // Sorted and filtered entries
  const sortedEntries = useMemo(() => {
    let filtered = [...entries]
    
    // Filter by rack
    if (filterRack) {
      filtered = filtered.filter(e => e.rack_code === filterRack)
    }
    
    // Filter by search term (judul)
    if (searchTerm) {
      filtered = filtered.filter(e => 
        e.judul?.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }
    
    // Sort
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'entry_date_desc':
          return new Date(b.entry_date) - new Date(a.entry_date)
        case 'entry_date_asc':
          return new Date(a.entry_date) - new Date(b.entry_date)
        case 'rack_asc':
          return (a.rack_code || '').localeCompare(b.rack_code || '')
        case 'rack_desc':
          return (b.rack_code || '').localeCompare(a.rack_code || '')
        case 'judul_asc':
          return (a.judul || '').localeCompare(b.judul || '')
        case 'judul_desc':
          return (b.judul || '').localeCompare(a.judul || '')
        case 'qty_desc':
          return (b.qty || 0) - (a.qty || 0)
        case 'qty_asc':
          return (a.qty || 0) - (b.qty || 0)
        default:
          return 0
      }
    })
    
    return filtered
  }, [entries, sortBy, filterRack, searchTerm])

  if (loading) return <Loading text="Memuat data gudang..." />

  const columns = [
    { header: 'Tanggal', render: (row) => new Date(row.entry_date).toLocaleDateString('id-ID') },
    { header: 'SPK', render: (row) => <span className="font-medium">{row.judul}</span> },
    { header: 'Batch', render: (row) => row.batch_number ? `#${row.batch_number}` : '-' },
    { header: 'Tipe', render: (row) => <Badge variant={row.entry_type === 'reguler' ? 'primary' : 'info'}>{row.entry_type}</Badge> },
    { header: 'Rak', accessor: 'rack_code' },
    { header: 'Qty', render: (row) => row.qty?.toLocaleString() },
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">📦 Manajemen Gudang</h1>
        <p className="text-gray-500">Monitor stok dan kapasitas rak gudang</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard icon="📦" label="Total SPK di Gudang" value={stats?.totalSpk || 0} color="blue" />
        <StatCard icon="📚" label="Total Qty" value={stats?.totalQty?.toLocaleString() || 0} color="green" />
        <StatCard icon="🗄️" label="Rak Tersedia" value={racks.length} color="purple" />
      </div>

      {/* Rack Capacity */}
      <Card title="🗄️ Kapasitas Rak">
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {racks.map((rack) => {
            const usedPercent = rack.kapasitas ? Math.round((rack.used / rack.kapasitas) * 100) : 0
            const isFull = usedPercent >= 80
            return (
              <div key={rack.kode} className={`p-4 rounded-lg border-2 ${isFull ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold">{rack.kode}</span>
                  {isFull && <span className="text-red-500 text-xs">Penuh</span>}
                </div>
                <div className="h-2 bg-gray-200 rounded-full overflow-hidden mb-1">
                  <div className={`h-full ${isFull ? 'bg-red-500' : 'bg-blue-500'}`} style={{ width: `${Math.min(usedPercent, 100)}%` }} />
                </div>
                <p className="text-xs text-gray-500">{rack.used?.toLocaleString()} / {rack.kapasitas?.toLocaleString()}</p>
                <p className="text-xs text-gray-400">{rack.lokasi}</p>
              </div>
            )
          })}
        </div>
      </Card>

      {/* Recent Entries with Sort & Filter */}
      <Card title="📋 Data Gudang">
        {/* View Mode Toggle */}
        <div className="flex items-center justify-between mb-4 pb-4 border-b">
          <div className="flex gap-2">
            <button
              onClick={() => setViewMode('entries')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                viewMode === 'entries' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <List className="w-4 h-4" />
              Entry Detail
            </button>
            <button
              onClick={() => setViewMode('summary')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                viewMode === 'summary' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Grid3X3 className="w-4 h-4" />
              Rekap Per Judul
            </button>
          </div>
        </div>

        {viewMode === 'entries' ? (
          <>
            {/* Sort & Filter Controls */}
            <div className="flex flex-wrap gap-3 mb-4 pb-4 border-b">
              {/* Search */}
              <div className="relative flex-1 min-w-[200px] max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Cari judul..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                />
              </div>
              
              {/* Filter by Rack */}
              <div className="min-w-[150px]">
                <select
                  value={filterRack}
                  onChange={(e) => setFilterRack(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Semua Rak</option>
                  {uniqueRacks.map(rack => (
                    <option key={rack} value={rack}>{rack}</option>
                  ))}
                </select>
              </div>
              
              {/* Sort */}
              <div className="min-w-[180px]">
                <div className="relative">
                  <ArrowUpDown className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 appearance-none bg-white"
                  >
                    <optgroup label="Waktu Input">
                      <option value="entry_date_desc">Terbaru</option>
                      <option value="entry_date_asc">Terlama</option>
                    </optgroup>
                    <optgroup label="Rak">
                      <option value="rack_asc">Rak A-Z</option>
                      <option value="rack_desc">Rak Z-A</option>
                    </optgroup>
                    <optgroup label="Judul">
                      <option value="judul_asc">Judul A-Z</option>
                      <option value="judul_desc">Judul Z-A</option>
                    </optgroup>
                    <optgroup label="Quantity">
                      <option value="qty_desc">Qty Terbanyak</option>
                      <option value="qty_asc">Qty Tersedikit</option>
                    </optgroup>
                  </select>
                </div>
              </div>
            </div>
            
            {/* Result count */}
            <p className="text-sm text-gray-500 mb-3">
              Menampilkan {sortedEntries.length} dari {entries.length} entry
              {filterRack && <span className="ml-1">(Rak: {filterRack})</span>}
              {searchTerm && <span className="ml-1">(Cari: "{searchTerm}")</span>}
            </p>
            
            <Table columns={columns} data={sortedEntries} emptyText="Tidak ada entry yang cocok" />
          </>
        ) : (
          <>
            {/* Summary by Title */}
            <p className="text-sm text-gray-500 mb-3">
              Menampilkan {byJudul.length} judul buku
            </p>
            
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Judul</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Kode</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-700">Qty SPK</th>
                    <th className="px-4 py-3 text-right font-medium text-gray-700">Di Gudang</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Rak</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700">Batch</th>
                    <th className="px-4 py-3 text-center font-medium text-gray-700">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {byJudul.map((item, idx) => {
                    const spkQty = Number(item.spk_qty) || 0
                    const warehouseQty = Number(item.total_warehouse_qty) || 0
                    const isComplete = warehouseQty >= spkQty
                    const percent = spkQty > 0 ? Math.round((warehouseQty / spkQty) * 100) : 0
                    
                    return (
                      <tr key={idx} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <a href={`/spk/${item.spk_id}`} className="font-medium text-blue-600 hover:underline">
                            {item.judul}
                          </a>
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-gray-600">{item.kode}</td>
                        <td className="px-4 py-3 text-right">{spkQty.toLocaleString()}</td>
                        <td className="px-4 py-3 text-right">
                          <span className={isComplete ? 'text-green-600 font-medium' : 'text-orange-600'}>
                            {warehouseQty.toLocaleString()}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {item.racks ? item.racks.split(', ').map((rack, i) => (
                              <span key={i} className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded">
                                {rack}
                              </span>
                            )) : '-'}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap gap-1">
                            {item.batches ? item.batches.split(', ').map((batch, i) => (
                              <span key={i} className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">
                                {batch}
                              </span>
                            )) : '-'}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex flex-col items-center gap-1">
                            <div className="w-full max-w-[80px] h-2 bg-gray-200 rounded-full overflow-hidden">
                              <div 
                                className={`h-full rounded-full ${isComplete ? 'bg-green-500' : 'bg-orange-500'}`}
                                style={{ width: `${Math.min(percent, 100)}%` }}
                              />
                            </div>
                            <span className={`text-xs ${isComplete ? 'text-green-600' : 'text-orange-600'}`}>
                              {percent}%
                            </span>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Card>
    </div>
  )
}

export default WarehousePage
